import { useState } from "react";
import { Seo } from "@/components/Seo";
import { Reveal, SectionHead, Panel, Tag, FlowStack } from "@/components/System";

const RUNTIME_FLOW = [
    { id: "vault", name: "DATA VAULT", desc: "Sensitive information is staged for processing. It enters the protected boundary and never leaves it raw." },
    { id: "runtime", name: "PROTECTED RUNTIME", desc: "The execution environment isolates the workload. Policy is enforced at the boundary." },
    { id: "agent", name: "AGENT", desc: "The agent reasons and acts over the private data, entirely inside the protected environment." },
    { id: "tee", name: "TEE", desc: "The Trusted Execution Environment provides hardware-level isolation for code and data in use." },
    { id: "attestation", name: "ATTESTATION", desc: "The environment produces a cryptographic statement about what executed and where." },
    { id: "evidence", name: "EVIDENCE", desc: "An evidence bundle leaves the boundary — commitments and proofs, never raw private data." },
];

const Runtime = () => {
    const [active, setActive] = useState("runtime");
    const current = RUNTIME_FLOW.find((s) => s.id === active);

    return (
        <div>
            <Seo path="/runtime" />
            <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
                <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl">
                    <div className="vm-micro mb-6 flex items-center gap-3">AGENTVM://RUNTIME <Tag tone="dev">EARLY DEVELOPMENT</Tag></div>
                    <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                        THE PROTECTED <span className="text-[#00D9FF] vm-glow">RUNTIME.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                        Sensitive information enters a protected execution environment. The agent operates inside that
                        environment. The external world does not need direct access to the underlying sensitive
                        information to verify important properties of execution.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
                <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr]">
                    <Reveal>
                        <div className="vm-micro-dim mb-4">SELECT A LAYER TO INSPECT</div>
                        <FlowStack steps={RUNTIME_FLOW} activeId={active} onSelect={setActive} />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Panel title={`RUNTIME://LAYER/${current.id.toUpperCase()}`} right="INSPECTOR" className="scanlines h-full" testid="runtime-inspector">
                            <div className="flex h-full flex-col p-6 sm:p-8">
                                <div className="font-mono text-[10px] tracking-[0.24em] text-[#00D9FF]/70">
                                    {String(RUNTIME_FLOW.findIndex((s) => s.id === active) + 1).padStart(2, "0")} / {String(RUNTIME_FLOW.length).padStart(2, "0")}
                                </div>
                                <h2 className="mt-3 font-mono text-2xl tracking-[0.12em] text-[#F5F9FF] sm:text-3xl">{current.name}</h2>
                                <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#B8C7D9] sm:text-base">{current.desc}</p>
                                <div className="mt-auto pt-10">
                                    <div className="vm-divider" />
                                    <div className="mt-4 flex justify-between font-mono text-[10px] tracking-[0.2em] text-[#B8C7D9]/60">
                                        <span>BOUNDARY: PROTECTED</span>
                                        <span className="text-emerald-300">FLOW: VERIFIED PATH</span>
                                    </div>
                                </div>
                            </div>
                        </Panel>
                    </Reveal>
                </div>
            </section>

            <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <SectionHead index="RTM.02" title="WHAT IS A TEE?" sub="The Trusted Execution Environment is the hardware foundation of the protected runtime — explained for both audiences." />
                    <div className="grid gap-5 md:grid-cols-2">
                        <Reveal>
                            <Panel title="FOR EVERYONE" right="PLAIN LANGUAGE" testid="tee-plain">
                                <div className="p-6 sm:p-8">
                                    <p className="text-sm leading-relaxed text-[#B8C7D9] sm:text-base">
                                        A TEE is a locked room inside the processor itself. When an agent works inside
                                        it, even the operator of the machine cannot read the data being processed.
                                        Crucially, the room can prove what software ran inside it — so you do not have
                                        to trust the machine's owner, only verify the proof.
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        <Tag tone="beta">CONFIDENTIALITY</Tag>
                                        <Tag tone="beta">INTEGRITY</Tag>
                                        <Tag tone="beta">PROVABILITY</Tag>
                                    </div>
                                </div>
                            </Panel>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Panel title="FOR DEVELOPERS" right="TECHNICAL" testid="tee-technical">
                                <div className="p-6 sm:p-8">
                                    <p className="text-sm leading-relaxed text-[#B8C7D9] sm:text-base">
                                        TDX-class confidential computing isolates a workload in a hardware-enforced
                                        trust domain with memory encryption and measured launch. The platform produces
                                        a signed quote over the measured state; remote attestation verifies the quote
                                        against known-good measurements, binding identity, code, and session to the
                                        hardware root of trust.
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        <Tag tone="dev">TDX</Tag>
                                        <Tag tone="dev">MEASURED BOOT</Tag>
                                        <Tag tone="dev">REMOTE ATTESTATION</Tag>
                                    </div>
                                </div>
                            </Panel>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="vm-panel-soft mt-5 p-6">
                            <div className="vm-micro mb-3">DESIGN PRINCIPLE</div>
                            <p className="text-lg font-semibold text-[#F5F9FF] sm:text-xl">
                                The outside world verifies <span className="text-[#00D9FF]">properties of execution</span> — never the private inputs.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Runtime;
